import { useState } from 'react';
import {
  BarChart3,
  Code2,
  LayoutTemplate,
  Mail,
  MapPinned,
  Megaphone,
  PenTool,
  PlugZap,
  Search,
  ShieldCheck,
  ShoppingBag,
  Store,
} from 'lucide-react';
import styled, { keyframes } from 'styled-components';

const offeringIcons = {
  webDevelopment: Code2,
  ecommerceDevelopment: ShoppingBag,
  shopifyDevelopment: Store,
  apiIntegrations: PlugZap,
  seoServices: Search,
  localSeo: MapPinned,
  paidMedia: Megaphone,
  reputationManagement: ShieldCheck,
  croServices: BarChart3,
  emailAutomation: Mail,
  brandIdentity: PenTool,
  landingPages: LayoutTemplate,
} as const;

type OfferingCard = {
  readonly title: string;
  readonly iconKey: keyof typeof offeringIcons;
  readonly accent: string;
};

const SCROLL_SEC = 42;
const CARD_SURFACE = 'linear-gradient(180deg, #f4f1ed 0%, #eef1f4 100%)';

const offerings: readonly OfferingCard[] = [
  {
    title: 'Custom Web Development Services',
    iconKey: 'webDevelopment',
    accent: '#203b62',
  },
  {
    title: 'Ecommerce Website Development',
    iconKey: 'ecommerceDevelopment',
    accent: '#6b4c4d',
  },
  {
    title: 'Shopify Store Development',
    iconKey: 'shopifyDevelopment',
    accent: '#2e5b54',
  },
  {
    title: 'Custom API Integration Services',
    iconKey: 'apiIntegrations',
    accent: '#775737',
  },
  {
    title: 'Search Engine Optimization Services',
    iconKey: 'seoServices',
    accent: '#203b62',
  },
  {
    title: 'Local SEO Services',
    iconKey: 'localSeo',
    accent: '#6b4c4d',
  },
  {
    title: 'Paid Media Management',
    iconKey: 'paidMedia',
    accent: '#2e5b54',
  },
  {
    title: 'Online Reputation Management',
    iconKey: 'reputationManagement',
    accent: '#775737',
  },
  {
    title: 'Conversion Rate Optimization Services',
    iconKey: 'croServices',
    accent: '#203b62',
  },
  {
    title: 'Email Marketing Automation',
    iconKey: 'emailAutomation',
    accent: '#6b4c4d',
  },
  {
    title: 'Brand Identity Design Services',
    iconKey: 'brandIdentity',
    accent: '#2e5b54',
  },
  {
    title: 'Landing Page Design Services',
    iconKey: 'landingPages',
    accent: '#775737',
  },
] as const;

const marquee = keyframes`
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(calc(-50% - (var(--track-gap) / 2)));
  }
`;

const Viewport = styled.div`
  position: relative;
  overflow: hidden;
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  padding: 1.7rem 0;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 1.7rem;
    bottom: 1.7rem;
    width: clamp(56px, 7vw, 120px);
    pointer-events: none;
    z-index: 2;
  }

  &::before {
    left: 0;
    background: linear-gradient(90deg, #efecf0 0%, rgba(239, 236, 240, 0) 100%);
  }

  &::after {
    right: 0;
    background: linear-gradient(270deg, #efecf0 0%, rgba(239, 236, 240, 0) 100%);
  }

  @media (max-width: 760px) {
    padding: 0.5rem 0;

    &::before,
    &::after {
      top: 0.5rem;
      bottom: 0.5rem;
    }
  }
`;

const Track = styled.div<{ $paused: boolean }>`
  --track-gap: clamp(1rem, 1.8vw, 1.35rem);
  display: flex;
  gap: var(--track-gap);
  width: max-content;
  animation: ${marquee} ${SCROLL_SEC}s linear infinite;
  animation-play-state: ${({ $paused }) => ($paused ? 'paused' : 'running')};
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Group = styled.div`
  display: flex;
  gap: clamp(1rem, 1.8vw, 1.35rem);
  width: max-content;
`;

const Card = styled.article<{ $accent: string }>`
  position: relative;
  overflow: hidden;
  isolation: isolate;
  width: clamp(250px, 22vw, 290px);
  min-height: 240px;
  padding: 1.3rem;
  border-radius: 26px;
  display: grid;
  align-content: space-between;
  gap: 1.2rem;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0.18) 100%),
    ${CARD_SURFACE};
  border: 1px solid rgba(23, 33, 47, 0.08);
  box-shadow:
    0 10px 20px rgba(17, 24, 39, 0.045),
    0 3px 8px rgba(17, 24, 39, 0.025);
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;

  &::after {
    content: '';
    position: absolute;
    right: -82px;
    bottom: -110px;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, ${({ $accent }) => `${$accent}18`} 0%, rgba(255, 255, 255, 0) 72%);
    z-index: 0;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-6px);
      border-color: rgba(23, 33, 47, 0.14);
      box-shadow:
        0 16px 28px rgba(17, 24, 39, 0.065),
        0 5px 12px rgba(17, 24, 39, 0.035);
    }
  }

  @media (max-width: 760px) {
    width: 74vw;
    min-height: 220px;
  }
`;

const IconPlate = styled.div<{ $accent: string }>`
  position: relative;
  z-index: 1;
  width: 68px;
  height: 68px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(23, 33, 47, 0.08);
  color: ${({ $accent }) => $accent};
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.05);
`;

const OfferingTitle = styled.h3`
  position: relative;
  z-index: 1;
  margin: 0;
  max-width: 12ch;
  color: #17212f;
  font-size: clamp(1.45rem, 2.4vw, 2rem);
  line-height: 1.02;
  letter-spacing: -0.035em;
  text-wrap: balance;
`;

function OfferingGroup({ items }: { items: readonly OfferingCard[] }) {
  return (
    <Group>
      {items.map((offering) => {
        const Icon = offeringIcons[offering.iconKey];

        return (
          <Card key={offering.title} $accent={offering.accent}>
            <IconPlate $accent={offering.accent} aria-hidden="true">
              <Icon size={34} strokeWidth={1.8} />
            </IconPlate>
            <OfferingTitle>{offering.title}</OfferingTitle>
          </Card>
        );
      })}
    </Group>
  );
}

type ServiceOfferingsCarouselProps = {
  readonly className?: string;
};

export default function ServiceOfferingsCarousel({ className }: ServiceOfferingsCarouselProps) {
  const [paused, setPaused] = useState(false);

  return (
    <Viewport
      className={className}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
    >
      <Track $paused={paused}>
        <OfferingGroup items={offerings} />
        <OfferingGroup items={offerings} />
      </Track>
    </Viewport>
  );
}
